/**
 * Machine slugs, read as English.
 *
 * Stable identifiers stay stored as kebab/snake case; the UI derives wording
 * rather than maintaining a label map that lags the product.
 *
 * The text is not translated: it is the identifier, spaced and cased.
 */

/**
 * Words title-casing would otherwise mangle. Keep this short — one entry earns
 * its place only when a real slug reads wrong without it.
 */
const ACRONYMS = new Set(["api", "bq", "id", "kpi", "llm", "sql"]);

const WORD_SEPARATORS = /[-_]+/;
const PLURAL_SUFFIX = "s";

function humanizeWord(word: string): string {
  const lower = word.toLowerCase();
  if (ACRONYMS.has(lower)) return lower.toUpperCase();
  // `kpis` is the plural of an acronym, not a word of its own — "KPIs", never
  // "Kpis" and never "KPIS".
  const singular = lower.slice(0, -PLURAL_SUFFIX.length);
  if (lower.endsWith(PLURAL_SUFFIX) && ACRONYMS.has(singular)) {
    return `${singular.toUpperCase()}${PLURAL_SUFFIX}`;
  }
  return lower;
}

/**
 * `admin-ops-sweep` → `Admin ops sweep`.
 *
 * Sentence case rather than title case: these run inside table cells beside
 * ordinary copy, and Title Case Every Word reads as a heading.
 */
export function humanizeSlug(slug: string): string {
  const words = slug.split(WORD_SEPARATORS).filter(Boolean).map(humanizeWord);
  const [first, ...rest] = words;
  if (first == null) return slug;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
}
